import { Component, input, output, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowStatesComponent } from "./workflow.states.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";

@TypeManagerDecorator("workflow-states-step")
@Component({
    selector: "workflow-states-step",
    template: `
        <div class="states-step-container">
            <div class="step-header">
                <h3>States</h3>
                <p class="step-description">
                    Configure states for each stage. States represent parallel approval checkpoints.
                </p>
            </div>

            <div class="add-state-section">
                <div class="add-controls">
                    <select [(ngModel)]="selectedStageForNewState" class="stage-dropdown">
                        <option value="">Select stage...</option>
                        @for (stage of workflowProcess().stages; track stage.workflowStageId || stage.id) {
                            <option [value]="stage.workflowStageId || stage.id">
                                {{ stage.stageName }}
                            </option>
                        }
                    </select>
                    <button
                        class="btn btn-primary"
                        [disabled]="!selectedStageForNewState"
                        (click)="addStateToSelectedStage()"
                    >
                        Add State
                    </button>
                </div>
            </div>

            <div class="states-list">
                @for (state of getAllStates(); track state.workflowStateId || state.id) {
                    <div class="state-item">
                        <div class="state-header" (click)="toggleStateExpansion(state)">
                            <div class="state-info">
                                <span class="state-name">{{ state.stateName }}</span>
                                <span class="stage-context">in {{ getStageForState(state)?.stageName }}</span>
                                <span class="state-flags">
                                    @if (state.defaultEnabled !== false) {
                                        <span class="flag enabled">Enabled</span>
                                    }
                                </span>
                            </div>
                            <div class="state-actions">
                                <span class="expand-icon" [class.expanded]="isStateExpanded(state)">
                                    {{ isStateExpanded(state) ? "▼" : "▶" }}
                                </span>
                            </div>
                        </div>

                        @if (isStateExpanded(state)) {
                            <div class="state-content">
                                <workflow-states
                                    [selectedStage]="getStageForState(state)"
                                    [workflowProcess]="workflowProcess()"
                                    (statesChange)="onStageStatesChange(getStageForState(state)!, $event)"
                                ></workflow-states>
                            </div>
                        }
                    </div>
                }
            </div>

            @if (getAllStates().length === 0) {
                <div class="no-states">
                    <p>No states defined. Select a stage above and click "Add State" to create your first state.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .states-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .step-description {
            margin: 0;
            color: #666;
            font-size: 14px;
        }

        .add-state-section {
            margin-bottom: 24px;
            padding: 16px;
            background: #f8f9fa;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
        }

        .add-controls {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .stage-dropdown {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid #ccc;
            border-radius: 4px;
            background: white;
        }

        .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
        }

        .btn-primary {
            background: #007bff;
            color: white;
        }

        .btn-primary:hover:not(:disabled) {
            background: #0056b3;
        }

        .btn-primary:disabled {
            background: #6c757d;
            cursor: not-allowed;
        }

        .states-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .state-item {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: visible;
            position: relative;
            z-index: 10;
        }

        .state-header {
            padding: 16px;
            background: #f8f9fa;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .state-header:hover {
            background: #e9ecef;
        }

        .state-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .state-name {
            font-weight: 500;
            color: #333;
        }

        .stage-context {
            font-size: 12px;
            color: #666;
            font-style: italic;
        }

        .state-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.enabled {
            background: #d4edda;
            color: #155724;
        }

        .expand-icon {
            color: #666;
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .state-content {
            padding: 20px;
            border-top: 1px solid #e0e0e0;
            overflow: visible;
            position: relative;
            z-index: 1;
        }

        .no-states {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, WorkflowStatesComponent, S25DropdownPaginatedModule],
})
export class WorkflowStatesStepComponent implements OnInit {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    workflowProcessChange = output<WorkflowTypes.WorkflowProcess>();

    selectedStageForNewState: string = "";
    private expandedStates = signal<Set<number | string>>(new Set());

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {}

    getAllStates(): WorkflowTypes.WorkflowState[] {
        const allStates: WorkflowTypes.WorkflowState[] = [];
        this.workflowProcess().stages?.forEach((stage) => {
            if (stage.states) {
                allStates.push(...stage.states);
            }
        });
        return allStates;
    }

    getStageForState(state: WorkflowTypes.WorkflowState): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.states?.some(
                    (s) =>
                        (s.workflowStateId && s.workflowStateId === state.workflowStateId) ||
                        (s.id && s.id === state.id),
                ),
            ) || null
        );
    }

    addStateToSelectedStage() {
        if (!this.selectedStageForNewState) return;

        const stage = this.workflowProcess().stages?.find(
            (s) =>
                (s.workflowStageId && s.workflowStageId.toString() === this.selectedStageForNewState) ||
                (s.id && s.id.toString() === this.selectedStageForNewState),
        );

        if (stage) {
            const newState: WorkflowTypes.WorkflowState = {
                id: this.getNextNegativeId(),
                workflowProcessId: this.workflowProcess().workflowProcessId,
                stateName: `State ${(stage.states || []).length + 1}`,
                defaultEnabled: true,
                description: "",
                contacts: [],
            };

            if (!stage.states) {
                stage.states = [];
            }
            stage.states.push(newState);

            this.workflowProcessChange.emit(this.workflowProcess());
            this.cd.detectChanges();
        }
    }

    private getNextNegativeId(): number {
        const allStates = this.getAllStates();
        return allStates.reduce((minId, state) => Math.min(minId, state.id || -1), -1) - 1;
    }

    toggleStateExpansion(state: WorkflowTypes.WorkflowState) {
        const stateKey = state.workflowStateId || state.id!;
        const expanded = this.expandedStates();

        if (expanded.has(stateKey)) {
            expanded.delete(stateKey);
        } else {
            expanded.add(stateKey);
        }

        this.expandedStates.set(new Set(expanded));
    }

    isStateExpanded(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.expandedStates().has(stateKey);
    }

    onStageStatesChange(stage: WorkflowTypes.WorkflowStage, states: WorkflowTypes.WorkflowState[]) {
        if (stage) {
            stage.states = states;
        }
        this.workflowProcessChange.emit(this.workflowProcess());
        this.cd.detectChanges();
    }
}
